<template>
  <div class="d-flex align-items-center justify-content-center min-vh-100 bg-white">
    <main class="form-signin w-100 m-auto text-center" style="max-width: 330px; padding: 1rem;">
      <form @submit.prevent="handleLogin">
        <h1 class="h3 mb-3 fw-normal">{{ $t('auth.loginTitle') }}</h1>

        <!-- Google 登入按鈕 -->
        <button 
          type="button" 
          @click="handleGoogleLogin"
          class="w-100 btn btn-lg btn-outline-secondary d-flex align-items-center justify-content-center mb-3"
          :disabled="loading"
        >
          <svg class="me-2" width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {{ $t('auth.googleSignIn') }}
        </button>

        <div class="text-center my-3">
          <span class="text-muted small">{{ $t('auth.orSignInWithEmail') }}</span>
        </div>

        <div class="form-floating mb-2">
          <input
            v-model="form.email"
            type="email"
            class="form-control"
            id="email"
            name="email"
            :placeholder="$t('auth.email')"
            aria-label="email-input"
            required
            autocomplete="email"
          >
          <label for="email">{{ $t('auth.email') }}</label>
        </div>
        
        <div class="form-floating mb-3">
          <input
            v-model="form.password"
            type="password"
            class="form-control"
            id="password"
            name="password"
            :placeholder="$t('auth.password')"
            aria-label="password-input"
            required
            autocomplete="current-password"
          >
          <label for="password">{{ $t('auth.password') }}</label>
        </div>

        <button
          class="w-100 btn btn-lg btn-primary"
          type="submit"
          aria-label="sign-in-button"
          :disabled="loading"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          {{ loading ? $t('common.loading') : $t('auth.loginTitle') }}
        </button>

        <div class="text-end mt-2">
          <router-link :to="getRoutePath('/reset-password')" class="text-muted small">
            {{ $t('auth.forgotPassword') }}
          </router-link>
        </div>

        <div v-if="error" class="alert alert-danger mt-3">
          {{ error }}
        </div>

        <p class="mt-4 text-muted">
          {{ $t('auth.noAccount') }} 
          <router-link :to="getRoutePath('/signup')">{{ $t('common.register') }}</router-link>
        </p>
      </form>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useRoutePath } from '../utils/routing'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const getRoutePath = useRoutePath() // 智能路由生成：根路徑返回 '/signup'，多語言返回 '/{lang}/signup'

// Form state
const form = ref({
  email: '',
  password: ''
})

const loading = ref(false)
const error = ref('')

/**
 * Firebase 錯誤訊息對應
 */
const getFirebaseErrorMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    'auth/user-not-found': t('auth.errors.userNotFound'),
    'auth/wrong-password': t('auth.errors.wrongPassword'), 
    'auth/invalid-credential': t('auth.errors.invalidCredential'),
    'auth/invalid-email': t('auth.errors.invalidEmail'),
    'auth/too-many-requests': t('auth.errors.tooManyRequests'),
    'auth/network-request-failed': t('auth.errors.networkError'),
    'auth/user-disabled': t('auth.errors.userDisabled')
  }
  
  return errorMessages[errorCode] || t('auth.errors.loginFailed')
}

const handleLogin = async () => {
  loading.value = true
  error.value = ''

  try {
    // 表單驗證
    if (!form.value.email) {
      throw new Error(t('auth.errors.emailRequired'))
    }
    if (!form.value.password) {
      throw new Error(t('auth.errors.passwordRequired'))
    }

    console.log('🔐 嘗試 Email/Password 登入:', form.value.email)
    
    // 使用 Auth Store 的登入方法，會自動處理 Firestore 整合
    const result = await authStore.signIn(form.value.email, form.value.password)
    
    if (result.success) {
      console.log('✅ SSG Email 登入成功，包含 Firestore 整合')
      
      // 登入成功，處理重定向
      const redirectPath = route.query.redirect as string
      if (redirectPath && redirectPath.startsWith('/app/')) {
        console.log('🔄 重定向到 SPA 應用:', redirectPath)
        window.location.href = redirectPath
      } else {
        console.log('🔄 重定向到預設儀表板')
        window.location.href = '/app'
      }
    } else {
      error.value = result.error || t('auth.errors.loginFailed')
    }
    
  } catch (err: any) {
    console.error('❌ SSG 登入失敗:', err)
    
    if (err.code) {
      // Firebase Auth 錯誤
      error.value = getFirebaseErrorMessage(err.code)
    } else {
      // 一般錯誤
      error.value = err.message || t('auth.errors.loginFailed')
    }
  } finally {
    loading.value = false
  }
}

/**
 * Google 登入處理
 * 使用 Auth Store 的 signInWithGoogle 方法確保 Firestore 整合
 */
const handleGoogleLogin = async () => {
  loading.value = true
  error.value = ''

  try {
    console.log('🔐 嘗試 Google 登入')
    
    // 使用 Auth Store 的 Google 登入方法，會自動處理 Firestore 整合
    const result = await authStore.signInWithGoogle()
    
    if (result.success) {
      console.log('✅ SSG Google 登入成功，包含 Firestore 整合')
      
      // 登入成功，處理重定向
      const redirectPath = route.query.redirect as string
      if (redirectPath && redirectPath.startsWith('/app/')) {
        console.log('🔄 重定向到 SPA 應用:', redirectPath)
        window.location.href = redirectPath
      } else {
        console.log('🔄 重定向到預設儀表板')
        window.location.href = '/app'
      }
    } else {
      error.value = result.error || t('auth.errors.googleSignInFailed')
    }
    
  } catch (err: any) {
    console.error('❌ SSG Google 登入失敗:', err)
    
    if (err.code) {
      // Firebase Auth 錯誤
      error.value = getFirebaseErrorMessage(err.code)
    } else {
      // 一般錯誤
      error.value = err.message || t('auth.errors.googleSignInFailed')
    }
  } finally {
    loading.value = false
  }
}
</script>